import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AequiaMark, Button, Icon, Input, ErrorBanner } from "@/components/ui";

function passwordPolicyErrors(password: string, confirm: string) {
  const errors: string[] = [];
  if (password.length < 12) errors.push("La nueva contraseña debe tener al menos 12 caracteres.");
  if (!/[A-Z]/.test(password)) errors.push("Debe incluir al menos una mayúscula.");
  if (!/[a-z]/.test(password)) errors.push("Debe incluir al menos una minúscula.");
  if (!/[0-9]/.test(password)) errors.push("Debe incluir al menos un número.");
  if (!/[^A-Za-z0-9]/.test(password)) errors.push("Debe incluir al menos un símbolo.");
  if (password !== confirm) errors.push("La confirmación no coincide.");
  return errors;
}

export default function ForceChangePasswordPage() {
  const { user, logout, changePassword } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const errors = passwordPolicyErrors(next, confirm);
    if (errors.length > 0) {
      setError(errors.join(" "));
      return;
    }
    setSaving(true);
    try {
      await changePassword(current, next, confirm);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo actualizar la contraseña.");
    } finally {
      setSaving(false);
    }
  }

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen bg-bg text-text flex items-center justify-center p-8">
      <form onSubmit={submit} className="w-full max-w-[440px]">
        <div className="flex flex-col items-center mb-6">
          <AequiaMark size={84} />
        </div>
        <div className="text-[11px] uppercase tracking-eyebrow text-muted font-semibold mb-2 text-center">
          Acceso restringido
        </div>
        <h1 className="font-serif text-3xl font-medium tracking-tightish mb-3 text-center">
          Cambia tu contraseña para continuar
        </h1>
        <p className="text-sm text-text-2 leading-relaxed mb-6 text-center">
          {user?.email
            ? `Hola ${user.email}, tu administrador requiere que actualices la contraseña antes de seguir.`
            : "Tu administrador requiere que actualices la contraseña antes de seguir."}
        </p>

        <Input
          label="Contraseña actual"
          type="password"
          inputSize="lg"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          autoComplete="current-password"
          required
        />
        <Input
          label="Nueva contraseña"
          type="password"
          inputSize="lg"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          autoComplete="new-password"
          required
        />
        <Input
          label="Confirmar nueva contraseña"
          type="password"
          inputSize="lg"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          required
        />

        {error && (
          <div className="mb-3">
            <ErrorBanner title="No se pudo actualizar la contraseña" message={error} />
          </div>
        )}

        <Button type="submit" variant="brand" size="lg" full loading={saving}>
          Actualizar contraseña
        </Button>

        <button
          type="button"
          onClick={handleLogout}
          className="mt-4 w-full text-center text-xs text-muted hover:text-text inline-flex items-center justify-center gap-1"
        >
          <Icon name="logout" size={13} /> Cerrar sesión
        </button>
      </form>
    </div>
  );
}
